/**
 * Phase 40 — the rules every reader of a pool shares: a lane's KEY (its
 * gateway + its sender IDs, order and case ignored; no IDs = the gateway +
 * its AI prompt), the duplicates that are refused, the words that say so
 * (by the rows' letters), which lane a drop belongs to, and where a drop on
 * a lane gets its senders.
 * For Media Devoted | Created By Ciro Autiero
 */
import type { DropGenSenderAiMode } from "../senders/static-senders";
import type { DropGenGatewayLane } from "./gateway-lane.types";
import { isLaneId } from "./parse-lanes";
import { senderGroupIds } from "./sender-group-ids";
export { senderGroupIds } from "./sender-group-ids";

export const laneLetter = (index: number): string => String.fromCharCode(65 + (index % 26));

/** Two lanes with the same key are the same thing twice. */
export const laneKey = (lane: Pick<DropGenGatewayLane, "routeId" | "senders">): string =>
  lane.senders.ids.length > 0
    ? `${lane.routeId}|${lane.senders.ids.map((s) => s.toUpperCase()).sort().join(",")}`
    : `${lane.routeId}|ai:${lane.senders.aiGroupIds ? [...lane.senders.aiGroupIds].sort().join(",") : lane.senders.aiGroupId ?? "default"}`;

/** [earlier index, later index] for every lane that repeats an earlier one's key. */
export function duplicateLanes(lanes: readonly DropGenGatewayLane[]): Array<[number, number]> {
  const first = new Map<string, number>();
  const out: Array<[number, number]> = [];
  lanes.forEach((lane, i) => {
    const key = laneKey(lane);
    const seen = first.get(key);
    if (seen === undefined) first.set(key, i);
    else out.push([seen, i]);
  });
  return out;
}

/** What the editor and the DTO refuse — empty when the pool is fine. */
export function lanesProblems(lanes: readonly DropGenGatewayLane[], routeName: (routeId: number) => string | null = () => null): string[] {
  const name = (lane: DropGenGatewayLane) => routeName(lane.routeId) ?? `route ${lane.routeId}`;
  const errs = duplicateLanes(lanes).map(([a, b]) =>
    lanes[a].senders.ids.length > 0
      ? `${laneLetter(a)} and ${laneLetter(b)} are both ${name(lanes[a])} with the same sender IDs — give one of them other IDs, another AI prompt, or remove it`
      : `${laneLetter(a)} and ${laneLetter(b)} are both ${name(lanes[a])} with no sender ID and the same AI prompt — give one of them sender IDs, another AI prompt, or remove it`,
  );
  lanes.forEach((lane, i) => {
    if (!isLaneId(lane.id)) errs.push(`lane ${laneLetter(i)}: the id must be 1–40 letters, digits, "_", ":" or "-"`);
  });
  return errs;
}

export const firstLaneOf = (lanes: readonly DropGenGatewayLane[], routeId: number | null): DropGenGatewayLane | null =>
  routeId === null ? null : (lanes.find((l) => l.routeId === routeId) ?? null);

/** The lane a draft belongs to: its own row id when that lane still exists on the draft's gateway, else the gateway's first lane. */
export function laneForDraft(lanes: readonly DropGenGatewayLane[], routeId: number | null, rowId: string | null | undefined): DropGenGatewayLane | null {
  const own = rowId ? (lanes.find((l) => l.id === rowId) ?? null) : null;
  if (own && (routeId === null || own.routeId === routeId)) return own;
  return firstLaneOf(lanes, routeId);
}

/** "23Telecom · AUSSIEWIN, CASINOAU" / "23Telecom · AI names" — how a lane is named everywhere. */
export const laneLabel = (lane: Pick<DropGenGatewayLane, "routeId" | "senders">, routeName: string | null): string =>
  `${routeName ?? `route ${lane.routeId}`} · ${lane.senders.ids.length > 0 ? lane.senders.ids.join(", ") : "AI names"}`;

export interface DropGenLaneSenderSource {
  /** The container's default AI sender group; null = the AI writes no names. */
  senderGroupId: string | null;
  senderGroupIds?: string[];
  aiMode: DropGenSenderAiMode;
}

/** A drop on `lane` sends with the lane's IDs; the AI adds names from the lane's group (or the default) where the mode says. */
export function sendersForLane(src: DropGenLaneSenderSource, lane: Pick<DropGenGatewayLane, "senders"> | null, position = 0): { fixed: string[]; aiGroupId: string | null } {
  const fixed = lane?.senders.ids ?? [];
  const groups = senderGroupsForLane(src, lane);
  const group = groups[position % groups.length] ?? null;
  const ai = !lane?.senders.staticOnly && group !== null && (fixed.length === 0 || (lane?.senders.aiMode ?? src.aiMode) === "ALWAYS");
  return { fixed, aiGroupId: ai ? group : null };
}

/** Per-gateway generation controls, falling back to existing saved defaults. */
export function senderSettingsForLane(src: { amount: number; mode: "SHARED" | "UNIQUE" }, lane: Pick<DropGenGatewayLane, "senders"> | null): { amount: number; mode: "SHARED" | "UNIQUE" } {
  return { amount: lane?.senders.amount ?? src.amount, mode: lane?.senders.mode ?? src.mode };
}

/** The prompts configured on a lane, also used by configuration-time generation. */
export function senderGroupsForLane(src: Pick<DropGenLaneSenderSource, "senderGroupId" | "senderGroupIds">, lane: Pick<DropGenGatewayLane, "senders"> | null): string[] {
  const own = lane?.senders;
  if (Array.isArray(own?.aiGroupIds)) return senderGroupIds(own.aiGroupIds);
  if (own?.aiGroupIds === undefined && own?.aiGroupId) return [own.aiGroupId];
  return senderGroupIds(src.senderGroupIds, src.senderGroupId);
}
